import { EventEmitter, Inject, Injectable, OnDestroy } from '@angular/core';

import { ToolboxService} from 'app/toolboxes/toolbox.service';
import { ProgressConstants } from 'core/constants';
import { ProductService } from 'core/product/product.service';
import { TranslationService } from 'core/translation/translation.service';
import { Utils } from 'core/utils';
import { Subscription } from 'rxjs';
import { ProductConfig } from '../product-designer-config';

export interface Progress
{
  id: string;
  group: string;
  groupMember: string | number;
  title: string;
}

interface OnChangeHook {
  id?: string;
  action(): Promise<void>;
}

@Injectable()
export class ProgressService implements OnDestroy
{
  private productConfig: ProductConfig;

  public progressChanged: EventEmitter<Progress> = new EventEmitter();

  private progressList: Array<Progress> = [];
  private currentProgress: Progress = {
      id: undefined, group: undefined, groupMember: undefined, title: ''
  };
  private _isFinalized: boolean = false;
  private changeCommitHooks: Array<OnChangeHook> = [];
  private subscriptions: Subscription = new Subscription();

  constructor(
      @Inject(ProductService) private productService,
      @Inject(ToolboxService) private toolboxService,
      @Inject(TranslationService) private translationService)
  {

    this.subscriptions.add(
        this.productService.productHasChanged.subscribe(
            async (productConfig) => {
          this.productConfig = productConfig;
          await this.determineProductProgress();
        }));

  }

  public ngOnDestroy(): void
  {
    this.subscriptions.unsubscribe();
  }

  public getProgressList(): Array<Progress>
  {
    return this.progressList;
  }

  public getPreviousProgress(): Progress
  {
    let previousProgress: Progress = undefined;
    for (let progress of this.progressList) {
      if (progress.id === this.currentProgress.id) {
        return previousProgress;
      }
      previousProgress = progress;
    }

    return undefined;
  }

  public addChangeHook(hook: OnChangeHook): void
  {
    if (hook.id) {
      this.changeCommitHooks = this.changeCommitHooks
          .filter((h) => h.id !== hook.id);
    }

    this.changeCommitHooks.push(hook);
  }

  public async commitChanges(): Promise<void>
  {
    const hooks = this.changeCommitHooks;
    this.changeCommitHooks = [];

    for (const hook of hooks) {
      await hook.action();
    }
  }

  public async setCurrentProgress(progressId: string): Promise<void>
  {
    await this.commitChanges();

    this.currentProgress = this.getProgressById(progressId);
    if (this.currentProgress) {
      this.toolboxService.closeActiveToolbox();
      this.progressChanged.emit(this.currentProgress);
    }

    Utils.scrollToPosition(0, 0);
  }

  public getCurrentProgress(): Progress
  {
    return this.currentProgress;
  }

  public getNextProgress(): Progress
  {
    let currentProgress: Progress;
    for (let progress of this.progressList) {
      if (currentProgress) {
        return progress;
      }
      else if (progress.id === this.currentProgress.id) {
        currentProgress = progress;
      }
    }

    return undefined;
  }

  public set isFinalized(isFinalized: boolean)
  {
    this._isFinalized = isFinalized;
  }

  public get isFinalized(): boolean
  {
    return this._isFinalized;
  }

  private async determineProductProgress(): Promise<void>
  {
    this.progressList = [];

    for (let stepId in this.productConfig.collageDesignerConfig.steps) {
      if (stepId in this.productConfig.collageDesignerConfig.steps) {
        let step = this.productConfig.collageDesignerConfig.steps[stepId];
        if (step.parts.some(p => p.templateGroups.length > 0)) {
          this.progressList.push(<Progress> {
              id: 'collagedesigner-' + stepId,
              group: ProgressConstants.GROUP_COLLAGEDESIGNER,
              groupMember: parseInt(stepId, 10),
              title: step.title
          });
        }
      }
    }

    for (let featureGroupId in this.productConfig.featureGroups) {
      if (featureGroupId in this.productConfig.featureGroups) {
        let featureGroup = this.productConfig.featureGroups[featureGroupId];
        this.progressList.push(<Progress> {
            id: 'features-' + featureGroupId,
            group: ProgressConstants.GROUP_FEATURES,
            groupMember: featureGroupId,
            title: featureGroup.name
        });
      }
    }

    if (this.productConfig.additionalProducts &&
        this.productConfig.additionalProducts.length > 0) {
      this.progressList.push(<Progress> {
          id: 'additionalProducts',
          group: ProgressConstants.GROUP_ADDITIONAL_PRODUCTS,
          groupMember: undefined,
          title: this.translationService
            .get('i18n.progress.stepAdditionalProducts')
      });
    }

    await this.setCurrentProgress(this.progressList[0].id);
  }

  private getProgressById(progressId: string): Progress
  {
    for (let progress of this.progressList) {
      if (progress.id === progressId) {
        return progress;
      }
    }
    return undefined;
  }
}
